<template>
  <div class="auth-callback-splash" role="status" aria-live="polite" aria-busy="true">
    <div class="auth-callback-splash__content">
      <img
        src="/images/project-blue-logo.png"
        alt="Project Blue"
        class="auth-callback-splash__logo"
        width="200"
        height="auto"
      />
      <p class="auth-callback-splash__message">{{ message }}</p>
      <div class="auth-callback-splash__progress-track">
        <div class="auth-callback-splash__progress-fill" />
      </div>
    </div>
  </div>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import { handleAuth0Callback, logoutFromAuth0 } from "@/composables/auth0";
import { sudoLogin, getUserByAuth0Id } from "@/composables/auth";
import { saveToUserLogs, userLogInputFields } from "@/composables/userLogs";
import {
  AUTH_ROUTES,
  AUTH_STORAGE_KEYS,
  AUTH_FAILURE_REASONS,
  HTTP_STATUS,
} from "@/constants/auth";

const route = useRoute();
const router = useRouter();
const message = ref("Completing sign in...");

onMounted(async () => {
  try {
    // 1. Check for Auth0 error in query parameters
    const searchParams = new URLSearchParams(window.location.search);
    const auth0Error = searchParams.get("error") || route.query.error;
    const auth0ErrorDesc = searchParams.get("error_description") || route.query.error_description;

    if (auth0Error) {
      console.error("Auth0 returned an error:", auth0Error, auth0ErrorDesc);
      let failureReason = AUTH_FAILURE_REASONS.SIGNIN_FAILURE;
      if (
        auth0Error === "organization_restriction" ||
        auth0ErrorDesc?.includes("organization")
      ) {
        failureReason = AUTH_FAILURE_REASONS.ORGANIZATION_RESTRICTION;
      }
      router.push({
        path: AUTH_ROUTES.AUTH_FAILURE,
        query: { type: failureReason },
      });
      return;
    }

    // 2. Process OAuth PKCE callback
    message.value = "Verifying authentication...";
    const { user, idToken } = await handleAuth0Callback();

    // 3. Sanitize browser address bar immediately
    if (typeof window !== "undefined" && window.history?.replaceState) {
      window.history.replaceState({}, document.title, window.location.origin + "/");
    }

    if (!user || !user.email) {
      throw new Error("Unable to retrieve authenticated user details from Auth0.");
    }

    // 4. Strict Invitation Email Verification (if in an onboarding flow)
    const storedInvite = localStorage.getItem(AUTH_STORAGE_KEYS.INVITE_DATA);
    if (storedInvite) {
      try {
        const inviteData = JSON.parse(storedInvite);
        if (
          inviteData?.email &&
          inviteData.email.trim().toLowerCase() !== user.email.trim().toLowerCase()
        ) {
          console.warn("Invited email does not match authenticated Auth0 email:", {
            invited: inviteData.email,
            authenticated: user.email,
          });

          // Clear invite data and log out
          localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_TOKEN);
          localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_DATA);

          await logoutFromAuth0(
            `${window.location.origin}/#${AUTH_ROUTES.AUTH_FAILURE}?type=${AUTH_FAILURE_REASONS.EMAIL_MISMATCH}`
          );
          return;
        }
      } catch (err) {
        console.error("Failed to parse stored invite data during callback:", err);
      }
    }

    // 5. Auto-link Auth0 user ID and retrieve User GUID from backend API
    message.value = "Connecting to your account...";
    const userRecord = await getUserByAuth0Id(user.sub, user.email);
    const userGuid = userRecord?.userGuid || userRecord?.UserGuid || "";

    // 6. Direct Token Exchange with Backend API
    message.value = "Setting up your session...";
    const credentials = {
      email: user.email,
      auth0id: user.sub,
      userGuid: userGuid,
    };

    const isAuthenticated = await sudoLogin(credentials);

    if (isAuthenticated) {
      // Clean up invite storage
      localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_TOKEN);
      localStorage.removeItem(AUTH_STORAGE_KEYS.INVITE_DATA);
      localStorage.setItem(AUTH_STORAGE_KEYS.USER_EMAIL, user.email);

      // Log success
      userLogInputFields.value = {
        userEmail: user.email,
        description: `User ${user.email} successfully logged in via Auth0.`,
        action: "Login",
        method: null,
        statusCode: HTTP_STATUS.OK,
        environment: window.location.hostname,
        endpoint: null,
        path: null,
      };
      await saveToUserLogs(userLogInputFields.value);

      message.value = "Welcome to Proxima!";
      router.push({ path: AUTH_ROUTES.DASHBOARD });
    } else {
      console.error("Token exchange failed for authenticated user:", user.email);
      router.push({
        path: AUTH_ROUTES.AUTH_FAILURE,
        query: { type: AUTH_FAILURE_REASONS.DEACTIVATED },
      });
    }
  } catch (error) {
    console.error("Fatal error during Auth0 callback processing:", error);
    router.push({
      path: AUTH_ROUTES.AUTH_FAILURE,
      query: { type: AUTH_FAILURE_REASONS.SIGNIN_FAILURE },
    });
  }
});
</script>

<style scoped>
.auth-callback-splash {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  width: 100vw;
  background-color: #ffffff;
  position: fixed;
  top: 0;
  left: 0;
  z-index: 9999;
}

.auth-callback-splash__content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 320px;
  padding: 2rem;
}

.auth-callback-splash__logo {
  max-width: 200px;
  height: auto;
  margin-bottom: 1.5rem;
}

.auth-callback-splash__message {
  font-family: "Gilroy", sans-serif;
  font-weight: 500;
  font-size: 16px;
  color: #333;
  text-align: center;
  margin: 0 0 1.25rem;
}

.auth-callback-splash__progress-track {
  width: 240px;
  height: 6px;
  background-color: rgba(33, 60, 229, 0.15);
  border-radius: 3px;
  overflow: hidden;
}

.auth-callback-splash__progress-fill {
  height: 100%;
  width: 40%;
  background: linear-gradient(to right, #213ce5, #2380f2);
  border-radius: 3px;
  animation: auth-callback-progress 1.5s ease-in-out infinite;
}

@keyframes auth-callback-progress {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
</style>
